import React from 'react'
// Plain RN Animated, NOT react-native-reanimated: this is a shared cross-platform
// component and reanimated's web build breaks the host bundle (runOnUISync /
// worklets version mismatch). RN Animated is provided by react-native-web on web.
// eslint-disable-next-line no-restricted-imports
import { Animated, Pressable } from 'react-native'

import { CheckIcon, CloseIcon, ErrorIcon, InfoIcon, WarningBoldIcon } from '../../icons'
import { colors } from '../../tokens'
import { Box } from '../box'
import { type IconSource } from '../icon'
import { Text } from '../text'

/**
 * Unified notification (toast) surface — matches Figma web `Toast`
 * (node 11797:122041) and mobile `Toast` (node 11798:142323). One component for
 * both platforms: the universal `Text` already renders Manrope, and the layout
 * flexes (full width on mobile, capped at 480 on web). Presentational only —
 * drive it through the gluestack `useToast`
 * hook (`render: () => <Notification … />`).
 */
export type NotificationStatus = 'success' | 'warning' | 'critical' | 'info' | 'neutral'

export interface NotificationProps {
    status?: NotificationStatus
    /** Message text. */
    children: React.ReactNode
    /** Optional inline action (e.g. "Go to issue"). Rendered when `actionLabel` is set. */
    actionLabel?: string
    onActionPress?: () => void
    /** Optional dismiss handler — renders the close (X) affordance when provided. */
    onClose?: () => void
    showIcon?: boolean
    /** Bottom status-coloured timeout bar (the auto-dismiss countdown). */
    showTimeoutBar?: boolean
    /**
     * Auto-dismiss time (ms) — animates the timeout bar from full to empty over
     * this duration (kept in sync with the toast's own dismiss timer). Omit / null
     * for a static full bar (no countdown).
     */
    duration?: number | null
}

// status → leading glyph + its colour (`tint` className + `iconColor` hex; the
// same hex also paints the timeout bar). The glyph is rendered DIRECTLY (not via
// the `Icon` wrapper, whose PrimitiveIcon only colours the stroke) as a
// `fill='currentColor'` SVG. createIcon bakes a dark `text-typography-950` base
// className onto it, so we BOTH pass the `tint` text-* class (twMerge strips the
// dark base) AND the `iconColor` hex via `color` (which createIcon forwards to the
// Svg as `currentColor`) — so the fill is the status colour whether or not the
// class resolves on native.
const STATUS_CONFIG: Record<NotificationStatus, { icon: IconSource; tint: string; color: string }> =
    {
        success: { icon: CheckIcon, tint: 'text-status-success', color: colors.status.success },
        warning: {
            icon: WarningBoldIcon,
            tint: 'text-status-warning',
            color: colors.status.warning,
        },
        critical: { icon: ErrorIcon, tint: 'text-status-critical', color: colors.status.critical },
        info: { icon: InfoIcon, tint: 'text-status-info', color: colors.status.info },
        neutral: { icon: InfoIcon, tint: 'text-status-neutral', color: colors.status.neutral },
    }

export const Notification: React.FC<NotificationProps> = ({
    status = 'success',
    children,
    actionLabel,
    onActionPress,
    onClose,
    showIcon = true,
    showTimeoutBar = true,
    duration = null,
}) => {
    const { icon: StatusGlyph, tint, color: statusColor } = STATUS_CONFIG[status]
    // Glyphs are `fill='currentColor'` SVGs; cast to the permissive IconSource so
    // we can pass `className`/`color`/`width`/`height` (createIcon's own type omits them).
    const CloseGlyph = CloseIcon as IconSource

    // Timeout bar counts down from full → empty over `duration` (in sync with the
    // toast's auto-dismiss): `progress` 1→0 drives the bar width %, shrinking it
    // left-anchored. A stay-until-dismissed toast (duration null) keeps a full bar.
    const progress = React.useMemo(() => new Animated.Value(1), [])
    React.useEffect(() => {
        if (!showTimeoutBar || !duration || duration <= 0) {
            return
        }
        const anim = Animated.timing(progress, { toValue: 0, duration, useNativeDriver: false })
        anim.start()
        return () => anim.stop()
    }, [progress, showTimeoutBar, duration])

    return (
        // Width: 480 on web / 320 on mobile (Figma web 11797:122041 + mobile
        // 11798:142323). A definite width lets the message Text wrap inside the
        // gluestack toast portal (which centre-aligns the toast, so a `w-full`
        // child has no width to resolve and never wraps).
        // gap-3 / pt-2 / pb-3 / px-3 / rounded-[4px] on bg/highlight, per Figma.
        <Box className='relative w-[320px] max-w-full flex-row items-center gap-3 rounded-[4px] bg-surface-highlight px-3 pb-3 pt-2 web:w-[480px]'>
            {showIcon && (
                <StatusGlyph className={tint} color={statusColor} width={24} height={24} />
            )}

            {/*
             * No line clamp: Figma (mobile Toast 11798:142323 / web) wraps the
             * message to as many lines as needed (word-break, the toast grows
             * vertically) — `numberOfLines` truncated longer messages so they
             * "didn't fit". The icon / action / close stay shrink-0 + centred.
             */}
            <Text className='flex-1 text-aos-text-default text-[14px] leading-[20px]'>
                {children}
            </Text>

            {actionLabel ? (
                <Pressable onPress={onActionPress} accessibilityRole='button'>
                    <Text className='text-aos-text-link text-[0.875rem] leading-[1.25rem] font-normal'>
                        {actionLabel}
                    </Text>
                </Pressable>
            ) : null}

            {onClose ? (
                <Pressable
                    onPress={onClose}
                    accessibilityRole='button'
                    accessibilityLabel='Dismiss'
                >
                    <CloseGlyph
                        className='text-aos-text-default'
                        color={colors['aos-text'].default}
                        width={24}
                        height={24}
                    />
                </Pressable>
            ) : null}

            {/*
             * Timeout progress bar (not a static border): shrinks left→right over
             * `duration` to show how long the toast stays. `statusColor` hex (a
             * Box-background class doesn't apply to Animated.View on native).
             */}
            {showTimeoutBar && (
                <Animated.View
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: 4,
                        borderBottomLeftRadius: 4,
                        borderBottomRightRadius: 4,
                        backgroundColor: statusColor,
                        width:
                            duration && duration > 0
                                ? progress.interpolate({
                                      inputRange: [0, 1],
                                      outputRange: ['0%', '100%'],
                                  })
                                : '100%',
                    }}
                />
            )}
        </Box>
    )
}

Notification.displayName = 'Notification'
